import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';
import { Loader2, MapPin, Phone, Mail } from 'lucide-react';

const PortalContact = () => {
    const [org, setOrg] = useState(null);
    const [contactEmail, setContactEmail] = useState(null); // orgemail-2b7e4d19 — resolved server-side (org email or owner fallback)
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrgDetails = async () => {
            const user = auth.currentUser;
            if (!user) return;

            try {
                const tokenResult = await user.getIdTokenResult();
                const orgId = tokenResult.claims.organizationId;
                if (!orgId) return;

                // o3-portalproj-9c4e7b21 — was a whole-org-doc read. The projection
                // serves every field this page renders.
                const getOrgInfo = httpsCallable(functions, 'getPublicOrganizationInfo');
                const orgResult = await getOrgInfo();
                setOrg(orgResult.data || {});
                try {
                    const res = await httpsCallable(functions, 'getPortalOrgContactEmail')();
                    setContactEmail(res.data?.email || null); // null -> email row hidden
                } catch (e) {
                    console.error("Error resolving org contact email:", e);
                }
            } catch (error) {
                console.error("Error fetching org details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrgDetails();
    }, []);

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-green-600" /></div>;

    // o3-portalproj-9c4e7b21 — the social block that used to sit here read
    // org.facebook / org.instagram / org.twitter. NOTHING in the product ever
    // writes those fields, so the block was permanently dead. Removed rather than
    // carried into the projection.
    return (
        <div className="max-w-3xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Contact Us</h1>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Contact Details</h2>
                    
                    <div className="space-y-6">
                        {/* Address */}
                        <div className="flex items-start gap-4">
                            <MapPin className="text-gray-400 mt-1" size={20} />
                            <div>
                                <p className="font-medium text-gray-900">Address</p>
                                {/* o3-portalproj-9c4e7b21 — this read org.address, which
                                    NOTHING writes, so every customer of every company saw
                                    the literal placeholder "123 Business Rd". The real
                                    field is address1 (+ optional address2), rendered the
                                    same way PortalInvoices.js does it. Rows are hidden
                                    when empty rather than showing invented placeholders. */}
                                <p className="text-gray-600">
                                    {org?.address1 && (
                                        <>{org.address1}{org.address2 ? `, ${org.address2}` : ''}<br /></>
                                    )}
                                    {[org?.city, org?.state, org?.zip].filter(Boolean).join(', ')}
                                </p>
                            </div>
                        </div>

                        {/* Phone — o3-portalproj-9c4e7b21: same family as the address
                            placeholder. This rendered a LIVE tel: link to the fake number
                            "(555) 123-4567" for any org with no phone on file, so hide the
                            row when empty, exactly as the Email row above already does. */}
                        {org?.phone && (
                        <div className="flex items-start gap-4">
                            <Phone className="text-gray-400 mt-1" size={20} />
                            <div>
                                <p className="font-medium text-gray-900">Phone</p>
                                <a href={`tel:${org.phone}`} className="text-blue-600 hover:underline">
                                    {org.phone}
                                </a>
                            </div>
                        </div>
                        )}

                        {/* Email — orgemail-2b7e4d19: resolved server-side (org email or
                            owner fallback); hidden entirely when nothing resolves, never a
                            placeholder address. */}
                        {contactEmail && (
                        <div className="flex items-start gap-4">
                            <Mail className="text-gray-400 mt-1" size={20} />
                            <div>
                                <p className="font-medium text-gray-900">Email</p>
                                <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">
                                    {contactEmail}
                                </a>
                            </div>
                        </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PortalContact;